import { createTheme } from "@mui/material/styles";

export const spacing = {
  xxs: "4px",
  xs: "8px",
  s: "12px",
  m: "20px",
  l: "32px",
  xl: "52px",
  xxl: "84px",
};

export const colors = {
  brand: "#162F54",
  brandDark: "#162F54",
  white: "#fff",
  greyLightest: "#f9f9f9",
  greyLighter: "#f6f6f6",
  greyLight: "#cfd2d4",
  greyDark: "#606A71",
  greyDarker: "#363E43",
  greyDarkest: "#1D2224",
  brown: "#b78751",
  danger: "#D32F2F",
  dangerLight: "#F36363",
  warning: "#FD8101",
  warningLight: "#F6C386",
  success: "#53B467",
  decorationSoft: "#fcc8b9",
  decorationHard: "#aadeca",
  decorationPurple: "#526AE3",
  shadow: "rgba(0, 0, 0, 0.1)",
  overlay: "rgba(2, 13, 66, 0.40)",
};

export const decorationColors = {
  decorationBrand: colors.brand,
  decorationBrandDark: colors.brandDark,
  decorationSoft: colors.decorationSoft,
  decorationHard: colors.decorationHard,
  decorationPurple: colors.decorationPurple,
  decorationBrown: colors.brown,
  decorationGreen: colors.success,
  decorationOrange: colors.warning,
  decorationRed: colors.danger,
  decorationRedLight: colors.dangerLight,
  decorationYellow: colors.warningLight,
};

export const utilityColors = {
  information: colors.brand,
  danger: colors.danger,
  success: colors.success,
  warning: colors.warning,
  shadow: colors.shadow,
  overlay: colors.overlay,
};

export const typographyColors = {
  defaultTitle: colors.greyDarker,
  defaultBody: colors.greyDark,
  inverseTitle: colors.white,
  inverseBody: colors.greyLightest,
};

export const uiColors = {
  background: colors.brand,
  backgroundDark: colors.brandDark,
  backgroundDarker: colors.greyDarker,
  backgroundDarkest: colors.greyDarkest,
  backgroundLightest: colors.greyLightest,
  border: colors.greyLight,
  borderInverse: colors.white,
  fieldBackground: colors.white,
  contentCards: colors.greyLighter,
};

export const stateColors = {
  focus: colors.brand,
  hover: colors.greyDark,
  empty: colors.greyLight,
  filled: colors.greyLighter,
  disabled: colors.greyLightest,
};

export const buttonColors = {
  primary: colors.brand,
  primaryDark: colors.brandDark,
  positive: colors.success,
  negative: colors.danger,
  warning: colors.warning,
  default: colors.greyLighter,
  textPrimary: colors.white,
  textDefault: colors.greyDark,
};

export const styles = {
  defaultFontFamily: {
    fontFamily: "Poppins !important",
    fontWeight: "400",
  },
  roundedCorners: { borderRadius: spacing.xxs },
  border: {
    boxShadow: `0 0 0 1px ${uiColors.border} inset`,
  },
  shadow: { boxShadow: `0 2px 5px ${utilityColors.shadow}` },
  transition: { transition: "all 150ms ease-in" },
  bodyText: {
    color: typographyColors.defaultBody,
    fontSize: 13,
    letterSpacing: "-0.3px",
    lineHeight: 2,
  },
  wrappingText: {
    lineHeight: 2,
  },
  bodyTextBold: {
    fontWeight: 600,
  },
  bodyTextItalic: {
    fontStyle: "italic",
  },
  bodyTextLarge: {
    fontSize: 15,
  },
  monospace: {
    fontFamily: "monospace",
    letterSpacing: "normal",
  },
  bodyTextSmall: {
    fontSize: 12,
  },
  whiteText: {
    color: typographyColors.inverseTitle,
    textShadow: `0px 1px 2px ${utilityColors.shadow}`,
  },
  whiteBodyText: {
    color: typographyColors.inverseBody,
    textShadow: `0px 1px 2px ${utilityColors.shadow}`,
  },
};

export const defaultTheme = {
  palette: {
    primary: {
      main: colors.brand,
      active: "#3A96FB",
      contrastText: colors.white,
    },
    secondary: {
      main: colors.brandDark,
      contrastText: colors.white,
    },
    error: {
      main: colors.danger,
      contrastText: colors.white,
    },
    warning: {
      main: colors.warning,
      contrastText: colors.white,
    },
    add: {
      main: "#41A11F",
      active: "#80C06A",
      contrastText: colors.white,
    },
    danger: {
      main: "#E44A4A",
      active: "#FF6666",
      contrastText: colors.white,
    },
    success: {
      main: colors.success,
      contrastText: colors.white,
    },
    border: {
      main: "#CCCCCC",
      hover: "#888888",
    },
    typography: {
      main: "#707070",
      inverse: colors.white,
      black: "#333333",
    },
    light: {
      main: colors.brandSoft,
      contrastText: colors.white,
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  typography: {
    whiteSpace: "pre-line",
    fontFamily: ["Poppins", "Roboto", "sans-serif"].join(","),
    body1: {
      color: "#707070",
      fontSize: 14,
      fontWeight: "medium",
      lineHeight: 2,
    },
    link: {
      color: colors.brand,
      ":hover": {
        color: "#3A96FB",
        cursor: "pointer",
      },
    },
    formTitle: {
      fontSize: "16px",
      fontWeight: "bold !important",
      color: "#CCCCCC",
      marginBottom: "16px",
    },
    h3: {
      fontWeight: 500,
    },
    h4: {
      color: "#333333",
      fontWeight: 500,
    },
  },
  components: {
    MuiFormControl: {
      variants: [
        {
          props: { variant: "fluid" },
          style: {
            width: "100%",
          },
        },
      ],
    },
    MuiButton: {
      defaultProps: {
        size: "large",
      },
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        ":disabled": {
          backgroundColor: "#E4E4E4",
        },
      },
      variants: [
        {
          props: { variant: "primary" },
          style: {
            ":hover": {
              backgroundColor: "#3A96FB",
            },
            ":disabled": {
              backgroundColor: "#CCCCCC",
              color: colors.white,
            },
            color: colors.white,
            backgroundColor: colors.brand,
            fontWeight: 500,
            height: 48,
          },
        },
        {
          props: { variant: "primary-inverse" },
          style: {
            ":hover": {
              backgroundColor: "#3A96FB",
              color: colors.white,
            },
            fontSize: 16,
            color: colors.brand,
            backgroundColor: colors.white,
            fontWeight: 500,
            height: 48,
          },
        },
        {
          props: { variant: "secondary" },
          style: {
            ":hover": {
              backgroundColor: "#F2F3F4",
              color: "#363E43",
            },
            color: "#363E43",
            backgroundColor: "#E0E1E2",
            fontWeight: 500,
            height: 48,
          },
        },
        {
          props: { variant: "add" },
          style: {
            ":hover": {
              backgroundColor: "#66A786",
              color: colors.white,
            },
            color: colors.white,
            backgroundColor: "#27883B",
            fontWeight: 500,
            minHeight: 0,
            maxHeight: 48,
          },
        },
        {
          props: { variant: "remove" },
          style: {
            ":hover": {
              backgroundColor: "#F24569",
              color: colors.white,
            },
            color: colors.white,
            backgroundColor: colors.danger,
            fontWeight: 500,
            minHeight: 0,
            maxHeight: 48,
          },
        },
        {
          props: { variant: "icon-squared" },
          style: {
            ":hover": {
              backgroundColor: "#3A96FB",
            },
            ":disabled": {
              backgroundColor: "#E4E4E4",
            },
            padding: 0,
            color: colors.white,
            backgroundColor: colors.brand,
            height: 48,
            minWidth: 48,
          },
        },
        {
          props: { variant: "outlined" },
          style: {
            ":hover": {
              bordercolor: colors.brand,
            },
            borderColor: colors.brand,
            minHeight: 0,
          },
        },
        {
          props: { variant: "social" },
          style: {
            ":hover": {
              backgroundColor: "#f7e4dd",
              borderColor: "#f0b57d",
            },
            fontFamily: "DM Serif Display",
            color: colors.brand,
            backgroundColor: "#fEf6f3",
            border: "1px solid #f7e4dd",
            fontWeight: "400",
            padding: `${spacing.s} ${spacing.m}`,
          },
        },
        {
          props: { variant: "light" },
          style: {
            ":hover": {
              backgroundColor: "#3A96FB",
            },
            ":disabled": {
              backgroundColor: "#CCCCCC",
              color: colors.white,
            },
            fontSize: 14,
            color: colors.white,
            backgroundColor: colors.brandSoft,
          },
        },
        {
          props: { variant: "light-outlined" },
          style: {
            ":hover": {
              borderColor: "#3A96FB",
            },
            // O texto acompanha a borda no cinza de desabilitado. Herdou
            // `colors.white` do variant `light`, que pinta o fundo de #CCCCCC
            // ao desabilitar — aqui não há fundo, então branco no branco
            // deixava o botão como um retângulo vazio.
            ":disabled": {
              borderColor: "#CCCCCC",
              color: "#CCCCCC",
            },
            fontSize: 14,
            border: "1px solid",
            borderColor: colors.brandSoft,
            color: colors.brandSoft,
          },
        },
      ],
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          "&.MuiSvgIcon-colorWarning": {
            color: "#FDBA01",
          },
          "&.MuiSvgIcon-colorSuccess": {
            color: "#2E7D32",
          },
        },
      },
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: "fileInput" },
          style: {
            color: "#162849",
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.5,
          },
        },
        {
          props: { variant: "captionError" },
          style: {
            color: "#B3261E",
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1.5,
          },
        },
      ],
    },
  },
};

const theme = createTheme(defaultTheme);

const dialogButtonSize = { minWidth: 92, height: 42, minHeight: 0 };

export const dialogVariants = {
  frontend: {
    wrapper: {
      sx: {
        borderRadius: "8px",
      },
      maxWidth: "730px",
      slotProps: {
        paper: { sx: { borderRadius: 2, maxWidth: "730px", width: "100%" } },
      },
      fullWidth: false,
      width: "100%",
    },
    title: {
      fontSize: 24,
      padding: 3,
      sx: { color: "#676A6C", backgroundColor: "#F9FAFB" },
    },
    content: {
      dividers: true,
      sx: {
        padding: "40px 24px",
      },
    },
    contentText: {
      fontWeight: 600,
      fontSize: 14,
      sx: {
        margin: 0,
        color: "#373E42",
      },
    },
    actions: {
      sx: {
        backgroundColor: "#F9FAFB",
        padding: "24px 16px",
      },
    },
    cancelButton: {
      variant: "outlined",
      sx: dialogButtonSize,
    },
    confirmButton: {
      variant: "remove",
      sx: dialogButtonSize,
    },
  },

  default: {
    wrapper: {
      maxWidth: "xs",
      fullWidth: true,
    },
    title: {
      sx: { color: "black", fontWeight: 500 },
    },
    content: {
      dividers: true,
    },
    contentText: {
      sx: { my: 1, color: "black" },
    },
    actions: {
      sx: { px: 3, py: 2 },
    },
    cancelButton: {
      variant: "light-outlined",
    },
    confirmButton: {
      variant: "light",
      autoFocus: true,
    },
  },
};

export const getDialogProps = (variant = "default") => {
  return dialogVariants[variant] || dialogVariants.default;
};

export default theme;
